<template>
    <dashboard-layout fullHeight>
        <template slot="title">
            <v-row no-gutters align="center">
                <v-col cols="auto"> <h3 class="tit grey-c--text">채용관리</h3> </v-col>
                <v-col cols="auto"> <span class="tit__line" /> </v-col>
                <v-col cols="auto">
                    <h3 class="tit">{{ recruitment?.subject }}</h3>
                </v-col>
            </v-row>
        </template>
        <template slot="contents">
            <v-row align-md="center" class="mb-n4 mb-md-12" style="flex: 0 0 auto">
                <v-col cols="12" class="mb-10 mb-md-0">
                    <v-row justify="space-between">
                        <v-col cols="12" md="auto">
                            <v-badge floating dot class="v-badge--btn">
                                <v-btn text @click="$router.go(-1)">
                                    <i class="icon icon-left-arrow mr-4 mr-lg-8" />
                                    <h4 class="tit--sm line-height-1">모집현황(목록)</h4>
                                </v-btn>
                            </v-badge>
                        </v-col>
                        <v-col cols="12" md="auto">
                            <application-list-filter v-bind="{ loading }" />
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12" class="mb-12 mb-lg-0">
                    <v-data-table v-bind="{ headers, items, itemClass }" disable-filtering disable-pagination disable-sort hide-default-footer @click:row="onClickRow">
                        <template v-for="header in headers.filter((header) => header.hasOwnProperty('formatter'))" #[`item.${header.value}`]="{ value }"> {{ header.formatter(value) }} </template>
                        <template #[`item.resume.personalInformation`]="{ item }">
                            <application-list-item-personal-information :value="item" v-bind="{ recruitment }" :ref="item._id" />
                        </template>
                        <template #[`item.stage`]="{ item }">
                            <application-list-item-stage :value="item" v-on="{ updateItem }" />
                        </template>
                        <template #footer>
                            <v-divider />
                            <v-row>
                                <v-col cols="12">
                                    <v-pagination :value="page" :length="pageCount" :total-visible="11" color="primary" @input="(page) => $router.push({ query: { ...$route.query, page } })"></v-pagination>
                                </v-col>
                            </v-row>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </template>
    </dashboard-layout>
</template>

<script>
import api from "@/api";
import { APPLICATION_STATES, RECRUITMENT_KANBAN_FIELDS } from "@/assets/variables";

import DashboardLayout from "@/components/dashboard/dashboard-layout.vue";
import ApplicationListFilter from "@/components/dashboard/company/application/application-list-filter.vue";
import ApplicationListItemPersonalInformation from "@/components/dashboard/company/application/application-list-item-personal-information.vue";
import ApplicationListItemStage from "@/components/dashboard/company/application/application-list-item-stage.vue";

const fields = Object.values(RECRUITMENT_KANBAN_FIELDS);

const initStage = () => fields.reduce((stage, { value }) => ({ ...stage, [value]: [] }), {});

export default {
    components: {
        DashboardLayout,
        ApplicationListFilter,
        ApplicationListItemPersonalInformation,
        ApplicationListItemStage,
    },
    props: {
        _recruitment: { type: String },
    },
    data: () => ({
        recruitment: undefined,
        applications: [],
        summary: { totalCount: 0 },

        selected: [],

        limit: 10,
        loading: false,
    }),
    computed: {
        headers() {
            return [
                { value: "appliedAt", text: "지원일시", formatter: (value) => value?.toDateTime?.() || value || "-", width: 120 },
                { value: "resume.personalInformation", text: "지원자" },
                { value: "stage", text: "지원상태", width: 160 },
            ];
        },
        items() {
            return this.applications.map((item, index) => ({ ...item, index: this.summary.totalCount - (this.page - 1) * this.limit - index }));
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        sort() {
            return this.$route.query.sort || JSON.stringify({ appliedAt: -1, _id: -1 });
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        params() {
            let { ...query } = this.$route.query;

            delete query.sort;
            delete query.page;

            return { ...query };
        },
    },
    mounted() {
        this.init().then(this.search);
    },
    watch: {
        _recruitment() {
            this.init().then(this.search);
        },
        params() {
            this.search();
        },
        page() {
            this.search();
        },
        sort() {
            this.search();
        },
    },
    methods: {
        async init() {
            this.loading = false;

            this.recruitment = undefined;
            this.applications = [];
            this.stage = initStage();

            this.selected = [];

            try {
                let { recruitment } = await api.v1.company.recruitments.get({
                    _id: this._recruitment,
                });
                this.recruitment = recruitment;
            } finally {
                this.loading = false;
            }
        },
        async search() {
            if (this.loading) return;
            else this.loading = true;

            try {
                let { params, skip, limit, sort } = this;
                let { summary, applications } = await api.v1.company.recruitments.applications.gets({
                    _recruitment: this._recruitment,
                    headers: { skip, limit, sort },
                    params,
                });
                this.summary = summary;
                this.applications = applications;
            } finally {
                this.loading = false;
            }
        },
        updateItem(item) {
            const index = this.applications.findIndex(({ _id }) => _id == item._id);
            if (0 <= index) this.applications.splice(index, 1, item);
        },
        toggleAllSelected() {
            if (this.isAllSelected) this.selected = [];
            else this.selected = [...this.recruits];
        },

        itemClass(item) {
            if (item?.isApplicantWithdrawn) return "withdrawn";
            if (item?.state == APPLICATION_STATES.IS_REVOKED.value) return "revoked";
        },

        onClickRow(item) {
            if (!this.itemClass(item)) return;
            this.$refs[item?._id]?.openApplication?.();
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    .v-data-table__wrapper {
        overflow: visible !important;
    }

    tr.revoked,
    tr.withdrawn {
        position: relative;

        &.revoked::after {
            content: "지원취소";
        }
        &.withdrawn::after {
            content: "탈퇴회원";
        }
        &::after {
            cursor: pointer;

            position: absolute;
            left: 0;
            bottom: 0;

            display: flex;
            justify-content: center;
            align-items: center;

            width: 100%;
            height: calc(100% - 1px);
            opacity: 0.25;
            background-color: black;

            color: white;
            font-size: 5rem;
            font-weight: bold;
        }
    }
}
</style>
